import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, MapPin, Globe, Calendar, ArrowLeft, Coffee, Clock, Check, ShieldCheck, MessageSquare } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import CertifiedBadge from "@/components/CertifiedBadge";
import ProfileReviewsSection from "@/components/profile/ProfileReviewsSection";
import { api, formatApiError } from "@/lib/api";
import { CoffeeChatDialog } from "@/components/marketplace/CoffeeChatDialog";
import { DemoCoffeeChatModal } from "@/components/marketplace/DemoCoffeeChatModal";
import { AdminMessageDialog } from "@/components/marketplace/AdminMessageDialog";
import ReportBlockMenu from "@/components/ReportBlockMenu";
import { useAuth } from "@/contexts/AuthContext";

export default function PublicProfilePage() {
  const { userId } = useParams();
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCoffee, setShowCoffee] = useState(false);
  const [showDemoCoffee, setShowDemoCoffee] = useState(false);
  const [showAdminMsg, setShowAdminMsg] = useState(false);
  const [chatStatus, setChatStatus] = useState(null); // {id, status} or null

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${userId}`);
        if (alive) setProfile(data);
      } catch (err) {
        if (alive) setError(formatApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  // Fetch any active coffee chat between the viewer and this profile so the
  // button reflects the real state (Request / Pending / Accepted).
  useEffect(() => {
    if (!me || !userId || me.id === userId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/coffee-chats/status", { params: { other_user_id: userId } });
        if (alive) setChatStatus(data.chat);
      } catch { /* silent — not critical for page render */ }
    })();
    return () => { alive = false; };
  }, [me, userId]);

  if (loading) {
    return <SiteShell><div className="container-page py-16 text-muted-foreground">Loading profile…</div></SiteShell>;
  }

  if (error || !profile) {
    return (
      <SiteShell>
        <div className="container-page py-16 text-center" data-testid="public-profile-not-found">
          <h1 className="font-serif text-3xl">Profile not available</h1>
          <p className="mt-3 text-muted-foreground max-w-md mx-auto">{error || "This profile isn't visible right now."}</p>
          <Link to="/search" className="btn-primary mt-6 inline-flex">Discover others</Link>
        </div>
      </SiteShell>
    );
  }

  const p = profile.profile || profile;
  const displayName = p.display_name || "(no name)";
  const roles = profile.roles || [];
  const bannerUrl = p.banner_url;
  const bannerPos = Number(p.banner_position_y ?? 50);
  const avatarUrl = p.avatar_url;
  const socials = p.social_links || {};

  // Coffee chat gating (mirrors the backend rule that chats are between an
  // author and a beta reader).
  const isSelf = me?.id === (profile.id || userId);
  const myRoles = new Set(me?.roles || []);
  const otherRoles = new Set(roles);
  const canRequestChat = !isSelf && (
    (myRoles.has("author") && otherRoles.has("beta_reader")) ||
    (myRoles.has("beta_reader") && otherRoles.has("author"))
  );
  // Demo profile detection — a regular user viewing a demo profile gets the
  // simulated "Try Demo Coffee Chat" flow instead of the real request button.
  // Admins keep the normal (or no) button so they can moderate.
  const isDemoProfile = !!profile.is_demo;
  const viewerIsAdmin = myRoles.has("admin");
  const showDemoCoffeeCta = !isSelf && isDemoProfile && !viewerIsAdmin;
  const showAdminMessage = !isSelf && viewerIsAdmin && !isDemoProfile;

  return (
    <SiteShell>
      <section className="container-page py-8" data-testid="public-profile-root">
        <Link to="/search" className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back to Discover
        </Link>

        {/* Banner */}
        <div
          className="mt-4 h-40 sm:h-56 rounded-3xl overflow-hidden bg-[hsl(var(--sb-moss))]"
          style={bannerUrl ? { backgroundImage: `url(${bannerUrl})`, backgroundSize: "cover", backgroundPosition: `50% ${bannerPos}%` } : undefined}
        />

        {/* Avatar overlaps banner; name/headline/roles ALWAYS render below the
            banner on both mobile and desktop. Prior layout used
            `sm:flex-row sm:items-end` on desktop, which placed the name text
            at the banner's baseline — on a light or gradient banner the
            white/dark text vanished. Consistent below-banner placement keeps
            the name readable regardless of banner colors. */}
        <div className="-mt-12 sm:-mt-16 px-4">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-background object-cover bg-muted" />
          ) : (
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-background bg-[hsl(var(--sb-forest))] text-white grid place-items-center font-serif text-4xl">
              {(displayName.charAt(0) || "?").toUpperCase()}
            </div>
          )}
        </div>
        <div className="mt-3 sm:mt-4 px-4">
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tight" data-testid="public-profile-name">{displayName}</h1>
            <CertifiedBadge
              isCertified={profile.is_certified}
              genres={profile.cert_genres}
              showGenres
            />
            {me && me.id !== userId && (
              <ReportBlockMenu
                targetUserId={userId}
                targetDisplayName={displayName}
                context="profile"
              />
            )}
          </div>
          {p.headline && (
            <p className="text-muted-foreground mt-1" data-testid="public-profile-headline">{p.headline}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-1">
            {roles.map((r) => (
              <span key={r} className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium border-[hsl(var(--sb-forest)/0.45)] bg-[hsl(var(--sb-forest)/0.14)] text-[hsl(var(--sb-forest-2))] dark:border-[hsl(var(--sb-forest)/0.55)] dark:bg-[hsl(var(--sb-forest)/0.22)] dark:text-[hsl(40_45%_92%)]">
                {r.replace("_", " ")}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            {p.bio && (
              <div>
                <h2 className="font-serif text-xl">About</h2>
                <p className="mt-2 text-sm whitespace-pre-wrap leading-relaxed" data-testid="public-profile-bio">{p.bio}</p>
              </div>
            )}

            {Array.isArray(p.genres) && p.genres.length > 0 && (
              <div>
                <h2 className="font-serif text-xl inline-flex items-center gap-2"><BookOpen size={18} /> Genres</h2>
                <div className="mt-2 flex flex-wrap gap-1">
                  {p.genres.map((g) => (
                    <span key={g} className="chip border border-[hsl(var(--sb-gold)/0.4)] bg-[hsl(var(--sb-gold)/0.1)] text-[hsl(var(--sb-gold-2))] text-xs">{g}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-4">
            {p.location && (
              <div className="rounded-xl border border-border p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><MapPin size={11} /> Location</div>
                <p className="mt-1 text-sm">{p.location}</p>
              </div>
            )}
            {profile.created_at && (
              <div className="rounded-xl border border-border p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><Calendar size={11} /> Joined</div>
                <p className="mt-1 text-sm">{new Date(profile.created_at).toLocaleDateString()}</p>
              </div>
            )}
            {(socials.website || socials.twitter || socials.instagram || socials.linkedin) && (
              <div className="rounded-xl border border-border p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><Globe size={11} /> Links</div>
                <ul className="mt-2 space-y-1 text-sm">
                  {socials.website && <li><a className="text-[hsl(var(--sb-forest-2))] hover:underline" href={socials.website} target="_blank" rel="noreferrer">Website</a></li>}
                  {socials.twitter && <li><a className="text-[hsl(var(--sb-forest-2))] hover:underline" href={socials.twitter} target="_blank" rel="noreferrer">Twitter/X</a></li>}
                  {socials.instagram && <li><a className="text-[hsl(var(--sb-forest-2))] hover:underline" href={socials.instagram} target="_blank" rel="noreferrer">Instagram</a></li>}
                  {socials.linkedin && <li><a className="text-[hsl(var(--sb-forest-2))] hover:underline" href={socials.linkedin} target="_blank" rel="noreferrer">LinkedIn</a></li>}
                </ul>
              </div>
            )}
            {showAdminMessage && (
              <button
                type="button"
                onClick={() => setShowAdminMsg(true)}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 min-h-[44px]"
                data-testid="public-profile-admin-message"
              >
                <MessageSquare size={16} /> Message
              </button>
            )}
            {isSelf ? (
              <p className="text-xs text-muted-foreground text-center">This is you.</p>
            ) : showDemoCoffeeCta ? (
              <button
                type="button"
                onClick={() => setShowDemoCoffee(true)}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="public-profile-try-demo-coffee-chat"
                title="Practice interaction — no real user is contacted."
              >
                <Coffee size={16} /> Try Demo Coffee Chat
              </button>
            ) : !canRequestChat ? (
              <div
                className="text-xs text-muted-foreground text-center rounded-lg bg-muted/40 border border-border/60 px-3 py-2"
                data-testid="public-profile-chat-unavailable"
              >
                Coffee chats are between an author and a beta reader. Add the other role in your profile to reach out.
              </div>
            ) : chatStatus?.status === "accepted" ? (
              <Link
                to="/messages"
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="public-profile-open-messages"
              >
                <Check size={16} /> Message directly
              </Link>
            ) : chatStatus?.status === "proposed" ? (
              <button
                type="button"
                disabled
                className="btn-ghost border w-full inline-flex items-center justify-center gap-2 cursor-not-allowed opacity-90"
                data-testid="public-profile-chat-pending"
                aria-label="Coffee chat request pending"
              >
                <Clock size={16} /> Coffee Chat Requested
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowCoffee(true)}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="public-profile-request-coffee-chat"
              >
                <Coffee size={16} /> Request Coffee Chat
              </button>
            )}
          </aside>
        </div>
        {showCoffee && (
          <CoffeeChatDialog
            otherUserId={userId}
            jobId={null}
            onClose={() => setShowCoffee(false)}
            onDone={(chat) => {
              setShowCoffee(false);
              if (chat) setChatStatus({ id: chat.id, status: chat.status });
            }}
          />
        )}
        {showAdminMessage && (
          <AdminMessageDialog
            open={showAdminMsg}
            onClose={() => setShowAdminMsg(false)}
            otherUserId={userId}
            displayName={displayName}
          />
        )}
        <DemoCoffeeChatModal
          open={showDemoCoffee}
          onClose={() => setShowDemoCoffee(false)}
          context={{ targetName: displayName }}
        />

        <ProfileReviewsSection userId={userId} displayName={displayName} />
      </section>
    </SiteShell>
  );
}
